// The design system's components (jarvis/design/system) as React components.
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';

/** A thread's own colour: a stable hue from its id (the design system: hsl(<hue> 80% var(--tl))). */
export function threadHue(id: string): number {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return [28, 165, 220, 330, 195, 280, 95, 0, 250, 140, 45, 305][h % 12]!;
}
export const tc = (id: string) => ({ '--tc': `hsl(${threadHue(id)} 80% var(--tl))` }) as CSSProperties;

export function Label({ children, c = 'cyan' }: { children: ReactNode; c?: string }) {
  return <div className={`j-label j-c-${c}`}>{children}</div>;
}

export function Panel({ children, c = 'cyan', className = '', style }: { children: ReactNode; c?: string; className?: string; style?: CSSProperties }) {
  return <div className={`j-panel j-c-${c} ${className}`} style={style}>{children}</div>;
}

export function JarvisCore({ size = 128 }: { size?: number }) {
  return <div className="j-core" style={{ width: size }}><img src="/img/hud-orb.webp" alt="Jarvis" /></div>;
}

/** Heat 0…∞ → ring 0…100. */
export function HeatRing({ heat, id }: { heat: number; id: string }) {
  const v = Math.max(3, Math.min(99, Math.round(100 * (1 - Math.exp(-heat / 4)))));
  const c = 2 * Math.PI * 18;
  return (
    <div className="j-heat" style={tc(id)}>
      <svg viewBox="0 0 44 44"><circle className="track" cx="22" cy="22" r="18" fill="none" strokeWidth="3" />
        <circle className="val" cx="22" cy="22" r="18" fill="none" strokeWidth="3" strokeDasharray={`${(c * v) / 100} ${c}`} /></svg>
      <span>{v}</span>
    </div>
  );
}

export type Theme = 'dark' | 'light' | 'auto';
export function useTheme(): [Theme, (t: Theme) => void] {
  const [t, setT] = useState<Theme>(() => (localStorage.getItem('theme') as Theme) || 'auto');
  useEffect(() => {
    const apply = () => { document.documentElement.dataset.theme = t === 'auto' ? (matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark') : t; };
    apply();
    localStorage.setItem('theme', t);
    const mq = matchMedia('(prefers-color-scheme: light)');
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [t]);
  return [t, setT];
}

export function ThemeSwitch() {
  const [t, setT] = useTheme();
  const opts: Array<[Theme, string]> = [['dark', 'Sötét'], ['light', 'Világos'], ['auto', 'Auto']];
  return (
    <div className="j-theme" role="group" aria-label="Téma">
      {opts.map(([k, l]) => <button key={k} type="button" aria-pressed={t === k} onClick={() => setT(k)}>{l}</button>)}
    </div>
  );
}

export function BottomNav({ route }: { route: string }) {
  const a = (href: string, label: string) => <a href={href} aria-current={route.startsWith(href) ? 'page' : undefined}>{label}</a>;
  return (
    <nav className="j-nav app-nav" aria-label="Fő navigáció">
      {a('#/most', 'Most')}{a('#/szalak', 'Szálak')}
      <a className="j-mic" href="#/kerdezz" aria-label="Kérdezz" style={{ backgroundImage: 'url(/img/hud-orb.webp)' }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></svg>
      </a>
      {a('#/graf', 'Gráf')}{a('#/kerdezz', 'Kérdezz')}
    </nav>
  );
}

const MONTHS = ['jan.', 'febr.', 'márc.', 'ápr.', 'máj.', 'jún.', 'júl.', 'aug.', 'szept.', 'okt.', 'nov.', 'dec.'];
/** 'nov. 6.' / 'ma' / 'holnap' / 'tegnap' */
export function day(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? iso + 'T12:00:00' : iso), t = new Date();
  const diff = Math.round((new Date(d.toDateString()).getTime() - new Date(t.toDateString()).getTime()) / 86400000);
  if (diff === 0) return 'ma';
  if (diff === 1) return 'holnap';
  if (diff === -1) return 'tegnap';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}.`;
}
export const ft = (n: number | null | undefined) => (n == null ? '' : `${Math.round(n).toLocaleString('hu-HU').replace(/ /g, ' ')} Ft`);

/** The Claude mark on a thread that has its own Claude workspace (project folder / Desktop project). */
export function ClaudeMark({ size = 16, title = 'Claude-munkaterület' }: { size?: number; title?: string }) {
  const rays = Array.from({ length: 12 }, (_, i) => i * 30);
  return (
    <svg className="claude-mark" width={size} height={size} viewBox="-12 -12 24 24" role="img" aria-label={title}>
      <title>{title}</title>
      {rays.map((a, i) => <rect key={a} x={-1.1} y={i % 2 ? -10 : -11.5} width={2.2} height={i % 2 ? 7.5 : 9} rx={1.1} transform={`rotate(${a})`} fill="currentColor" />)}
    </svg>
  );
}
